import { type IstanzaSezioneDati } from '@framework/api-client/api/backoffice/schemas/istanzaSezioneDati.ts';
import { FillableWidgetList } from '@framework/engine/schema/form/fillable/FillableWidgetList.tsx';
import { WidgetScopeContextProvider } from '@framework/engine/schema/form/fillable/WidgetScopeContext.tsx';
import { type IRootWidget } from '@framework/engine/schema/widgets/repository/containers/root/IRootWidget.ts';
import { produce } from 'immer';
import { useMemo } from 'react';
import { sectionMigrationEngine } from '../../engine/migration/SectionMigrationEngine.ts';
import { DeclarationTitle } from './DeclarationTitle.tsx';
import { DeclarationWrapper } from './DeclarationWrapper.tsx';

export interface IProcedureDeclarationSectionProps {
  declaration: IstanzaSezioneDati;
}

/**
 * Sezione dati di un procedimento in sola lettura.
 *
 * Applica automaticamente le migrazioni del
 * SectionMigrationEngine al widget tree prima del rendering.
 */
export function ProcedureDeclarationSection(
  props: IProcedureDeclarationSectionProps,
) {
  const { declaration } = props;

  /**
   * Widget figli della root, con migrazioni applicate
   * se il versionId è inferiore a quello attuale.
   */
  const declarationWidgets = useMemo(() => {
    const root = declaration.jsonCampi as IRootWidget | undefined;
    if (!root) return [];

    if (sectionMigrationEngine.needMigration(root)) {
      const migrated = produce(root, draft => {
        sectionMigrationEngine.run(draft);
      });
      return migrated.children ?? [];
    }

    return root.children ?? [];
  }, [declaration.jsonCampi]);

  return (
    <DeclarationWrapper key={declaration.codiceSezioneDati}>
      <DeclarationTitle
        declaration={declaration}
        title={declaration.titoloSezioneDati ?? 'Dichiarazione'}
      />
      <WidgetScopeContextProvider prefix={`${declaration.idSezioneDati}.`}>
        <FillableWidgetList widgets={declarationWidgets} readonly />
      </WidgetScopeContextProvider>
    </DeclarationWrapper>
  );
}
